export type Appearance = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

export const APPEARANCE_STORAGE_KEY = 'appearance'
export const LEGACY_APPEARANCE_STORAGE_KEY = 'kilio-theme'

export function isAppearance(value: unknown): value is Appearance {
  return value === 'light' || value === 'dark' || value === 'system'
}

export function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') {
    return 'light'
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function resolveTheme(appearance: Appearance): ResolvedTheme {
  if (appearance === 'system') {
    return getSystemTheme()
  }

  return appearance
}

export function getStoredAppearance(): Appearance {
  if (typeof window === 'undefined') {
    return 'system'
  }

  try {
    const stored = window.localStorage.getItem(APPEARANCE_STORAGE_KEY)
    if (isAppearance(stored)) {
      return stored
    }

    const legacy = window.localStorage.getItem(LEGACY_APPEARANCE_STORAGE_KEY)
    if (legacy === 'light' || legacy === 'dark') {
      return legacy
    }
  } catch {
    // localStorage can throw in restricted browsing modes
  }

  return 'system'
}

export function persistAppearance(appearance: Appearance) {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.localStorage.setItem(APPEARANCE_STORAGE_KEY, appearance)
    window.localStorage.removeItem(LEGACY_APPEARANCE_STORAGE_KEY)
  } catch {
    // Ignore quota / privacy-mode failures; the in-memory theme still applies.
  }
}

export function applyResolvedTheme(resolved: ResolvedTheme) {
  if (typeof document === 'undefined') {
    return
  }

  const root = document.documentElement
  root.classList.remove('light', 'dark')
  root.classList.add(resolved)
  root.style.colorScheme = resolved
}

export function initializeTheme() {
  applyResolvedTheme(resolveTheme(getStoredAppearance()))
}
